import React, { useEffect, useState } from 'react';

function TopBtn({ active, title, onClick, children, badge }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`relative m-0 inline-flex h-7 items-center gap-1.5 rounded-sm border px-2 text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors shrink-0 ${
        active
          ? 'border-yellow-400 bg-yellow-500 text-black'
          : 'border-transparent text-yellow-500/90 hover:border-yellow-600/40 hover:bg-yellow-500/10 hover:text-yellow-300'
      }`}
    >
      {children}
      {badge ? (
        <span className="ml-0.5 rounded bg-yellow-500 px-1 py-px text-[8px] font-bold text-black">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

/**
 * Horizontal scene tools in the app header.
 */
export default function SceneTopbarTools({
  isScribbleMode,
  onToggleScribble,
  showPanel,
  onTogglePanel,
  showMeasurements,
  onToggleMeasurements,
  loadingCount = 0,
  onShowLoading,
  showViews,
  onToggleViews,
  showSearch,
  onToggleSearch,
  onScreenshot,
  showPreferences,
  onTogglePreferences,
}) {
  const [isFullscreen, setIsFullscreen] = useState(
    () => typeof document !== 'undefined' && Boolean(document.fullscreenElement)
  );

  useEffect(() => {
    const onFs = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch (err) {
      console.warn('[fullscreen]', err?.message || err);
    }
  };

  const reloadPage = () => {
    window.location.reload();
  };

  return (
    <nav
      className="flex items-center gap-0.5 min-w-0 overflow-x-auto scrollbar-none"
      aria-label="Scene tools"
    >
      <TopBtn title="Layers panel (L)" active={showPanel} onClick={onTogglePanel}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <path d="M4 6h16M4 12h16M4 18h10" strokeLinecap="round" />
        </svg>
        <span className="hidden md:inline">Layers</span>
      </TopBtn>

      <TopBtn title="Tools" active={showMeasurements} onClick={onToggleMeasurements}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <path d="M4 20L20 4M8 20h4M12 16h4M4 12v4" strokeLinecap="round" />
        </svg>
        <span className="hidden md:inline">Tools</span>
      </TopBtn>

      <TopBtn title="Draw" active={isScribbleMode} onClick={onToggleScribble}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <path d="M4 20l4.5-1.2L19 8.3a2.1 2.1 0 00-3-3L5.5 15.8 4 20z" strokeLinejoin="round" />
        </svg>
        <span className="hidden md:inline">Draw</span>
      </TopBtn>

      <TopBtn title="Find layer (/ )" active={showSearch} onClick={onToggleSearch}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <circle cx="11" cy="11" r="6.5" />
          <path d="M16 16l4 4" strokeLinecap="round" />
        </svg>
        <span className="hidden md:inline">Find</span>
      </TopBtn>

      <TopBtn title="Saved views" active={showViews} onClick={onToggleViews}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <path d="M4 7h16M4 12h10M4 17h16" strokeLinecap="round" />
          <circle cx="18" cy="12" r="1.6" fill="currentColor" />
        </svg>
        <span className="hidden md:inline">Views</span>
      </TopBtn>

      <TopBtn title="Screenshot (S)" onClick={onScreenshot}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <rect x="3" y="7" width="18" height="13" rx="1.5" />
          <circle cx="12" cy="13.5" r="3" />
          <path d="M9 7l1.2-2h3.6L15 7" strokeLinecap="round" />
        </svg>
        <span className="hidden lg:inline">Shot</span>
      </TopBtn>

      {loadingCount > 0 && onShowLoading && (
        <TopBtn title="Show loading list" badge={String(loadingCount)} onClick={onShowLoading}>
          <svg
            viewBox="0 0 24 24"
            className="h-3.5 w-3.5 animate-spin"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
          >
            <path d="M12 3a9 9 0 019 9" strokeLinecap="round" />
          </svg>
          <span className="hidden lg:inline">Loading</span>
        </TopBtn>
      )}

      <span className="mx-0.5 h-4 w-px bg-yellow-600/25 shrink-0" aria-hidden />

      <TopBtn title="Preferences" active={showPreferences} onClick={onTogglePreferences}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2.5v2.2M12 19.3v2.2M4.9 4.9l1.6 1.6M17.5 17.5l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.9 19.1l1.6-1.6M17.5 6.5l1.6-1.6" strokeLinecap="round" />
        </svg>
        <span className="hidden md:inline">Preferences</span>
      </TopBtn>

      <TopBtn
        title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        active={isFullscreen}
        onClick={toggleFullscreen}
      >
        {isFullscreen ? (
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
            <path d="M9 9H4V4M15 9h5V4M9 15H4v5M15 15h5v5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
            <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        <span className="hidden lg:inline">{isFullscreen ? 'Exit' : 'Full'}</span>
      </TopBtn>

      <TopBtn title="Reload page" onClick={reloadPage}>
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.7">
          <path
            d="M4 12a8 8 0 0113.66-5.66M20 12a8 8 0 01-13.66 5.66"
            strokeLinecap="round"
          />
          <path d="M17 4v4h-4M7 20v-4h4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="hidden lg:inline">Reload</span>
      </TopBtn>
    </nav>
  );
}
